/**
 * Every drop-down in the New request sheet, one way (Aaron, step 28: "all of
 * the drop downs, related or otherwise, should function similar in this
 * form").
 *
 * Tab into it and it is open, the way Lead source always was; typing filters
 * the list; ↑ and ↓ move; Enter or Space takes the row you are on; Esc closes
 * it and Tab moves on without changing anything. Where a list takes new
 * values (a lead source, a company) Space types a space once something has
 * been typed, because a two-word name is the whole point of typing there.
 *
 * A desktop control: a phone keeps its bottom sheets (§16.3), which have no
 * keyboard to speak of.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { IconChevronDown, IconPlus } from '../frame/icons.js'

export interface KeyOption {
  /** `''` stands for "none" — a clearable list's first row. */
  value: string
  label: string
  /** A tile or glyph before the label, drawn in the box too once chosen. */
  lead?: ReactNode
  /** Right-aligned and faint: a count. */
  detail?: string
  /** A muted line under the label. */
  sub?: string
}

export function KeySelect({
  label, value, display, options, placeholder = 'Choose one', onChange, onAdd, addWords, invalid,
  field, id, describedBy, className,
}: {
  /** The screen-reader name. */
  label: string
  value: string | null
  /** What the box says when the value is not one of the options (a new lead source). */
  display?: string | null
  options: KeyOption[]
  placeholder?: string
  onChange: (value: string | null) => void
  /** Offered as the last row for typed text that matches nothing. */
  onAdd?: (text: string) => void
  addWords?: (text: string) => string
  invalid?: boolean
  /** `data-request-field`, so the sheet can focus it. */
  field?: string
  id?: string
  describedBy?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState<string | null>(null)
  const [active, setActive] = useState(0)
  const box = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)

  const chosen = options.find((option) => option.value === (value ?? '')) ?? null
  const typed = (text ?? '').trim()
  const shown = useMemo(() => {
    const wanted = typed.toLowerCase()
    return wanted ? options.filter((option) => option.label.toLowerCase().includes(wanted)) : options
  }, [options, typed])
  const exact = options.some((option) => option.label.toLowerCase() === typed.toLowerCase())
  const canAdd = Boolean(onAdd) && typed !== '' && !exact
  const rows = shown.length + (canAdd ? 1 : 0)
  /*
   * The row Enter takes is always one that is shown: typing can shrink the
   * list under a highlight that was further down it.
   */
  const at = rows === 0 ? 0 : Math.min(active, rows - 1)

  /* Opening starts on what is chosen, so ↓ goes on from it rather than from the top. */
  const openAt = () => {
    const found = shown.findIndex((option) => option.value === (value ?? ''))
    setActive(Math.max(found, 0))
    pointer.current = null
    setOpen(true)
  }
  /*
   * Only a pointer that has moved takes the highlight. A list that opens
   * under a resting pointer gets hover events — and in Chromium a mouse-move
   * — without anybody touching the mouse, and that must not steal the row the
   * keyboard is on (step 28: Tab, "hard", Enter chose nothing).
   */
  const pointer = useRef<string | null>(null)
  const hover = (index: number) => (event: { clientX: number; clientY: number }) => {
    const at = `${event.clientX},${event.clientY}`
    const moved = pointer.current !== null && pointer.current !== at
    pointer.current = at
    if (moved && active !== index) setActive(index)
  }
  useEffect(() => { if (text !== null) setActive(0) }, [typed])

  useEffect(() => {
    if (!open) return
    const outside = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) { setOpen(false); setText(null) }
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [open])

  /* The row you are on stays in view. */
  useEffect(() => {
    if (!open) return
    list.current?.querySelector<HTMLElement>(`[data-row="${at}"]`)?.scrollIntoView?.({ block: 'nearest' })
  }, [open, at])

  const choose = (index: number) => {
    if (index < shown.length) onChange(shown[index]!.value || null)
    else if (canAdd) onAdd?.(typed)
    setOpen(false)
    setText(null)
  }

  const words = text ?? (chosen ? chosen.label : display ?? '')

  return (
    <div ref={box} className={cn('relative', className)}>
      {/*
        * The whole box is the control: a press on the chevron (or the tile, or
        * the padding) opens the list, and on the chevron closes it again
        * (Aaron, step 28: "clicking directly on the chevron doesn't open or
        * close the options"). The text box keeps the focus either way.
        */}
      <span
        onMouseDown={(event) => {
          if (event.target === input.current) return
          event.preventDefault()
          if (open) { setOpen(false); setText(null); return }
          input.current?.focus()
          openAt()
        }}
        className={cn('flex h-9 w-full cursor-pointer items-center gap-1.5 rounded-control border bg-surface px-[11px]',
        'text-[13.5px] font-semibold text-text',
        invalid ? 'border-danger'
          : open ? 'border-accent-line ring-2 ring-accent-ring' : 'border-line hover:border-line-strong')}>
        {text === null && chosen?.lead && <span aria-hidden className="flex shrink-0">{chosen.lead}</span>}
        <input
          ref={input}
          id={id}
          value={words}
          placeholder={placeholder}
          aria-label={label}
          aria-describedby={describedBy}
          aria-expanded={open}
          aria-autocomplete="list"
          aria-invalid={invalid || undefined}
          role="combobox"
          data-request-field={field}
          autoComplete="off"
          onFocus={(event) => { openAt(); event.target.select() }}
          onMouseDown={() => { if (!open) openAt() }}
          onChange={(event) => { setText(event.target.value); setOpen(true) }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              if (!open) { openAt(); return }
              setActive((was) => Math.min(Math.min(was, rows - 1) + 1, rows - 1))
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              if (!open) { openAt(); return }
              setActive((was) => Math.max(Math.min(was, rows - 1) - 1, 0))
            } else if (event.key === 'Enter' && open && rows > 0) {
              event.preventDefault()
              choose(at)
            } else if (event.key === ' ' && open && rows > 0 && !(onAdd && typed !== '')) {
              event.preventDefault()
              choose(at)
            } else if (event.key === 'Escape' && open) {
              event.stopPropagation()
              setOpen(false)
              setText(null)
            } else if (event.key === 'Tab') {
              setOpen(false)
              setText(null)
            }
          }}
          className="min-w-0 flex-1 bg-transparent outline-none focus-visible:shadow-none placeholder:font-normal placeholder:text-text-faint"
        />
        <span className="shrink-0 text-text-muted"><IconChevronDown size={14} /></span>
      </span>
      {open && (
        <div className="absolute inset-x-0 top-full z-30 mt-1.5">
          <div ref={list} role="listbox" aria-label={label}
            className="max-h-[280px] overflow-y-auto rounded-[12px] border border-line bg-surface py-1 shadow-popover">
            {shown.map((option, index) => (
              <button
                key={option.value || 'none'}
                type="button"
                role="option"
                tabIndex={-1}
                data-row={index}
                aria-selected={index === at}
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={hover(index)}
                onClick={() => choose(index)}
                className={cn('mx-1 flex min-h-[34px] w-[calc(100%-8px)] items-center gap-2.5 rounded-button px-3 py-1',
                  'text-left text-[13px] text-text', index === at && 'bg-hover')}
              >
                {option.lead && <span aria-hidden className="flex shrink-0">{option.lead}</span>}
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className={cn('truncate', option.value === (value ?? '') && 'font-semibold')}>
                    {option.label}
                  </span>
                  {option.sub && <span className="truncate text-[11.5px] text-text-muted">{option.sub}</span>}
                </span>
                <span className="flex-1" />
                {option.detail && <span className="text-[11.5px] text-text-faint">{option.detail}</span>}
              </button>
            ))}
            {canAdd && (
              <>
                {shown.length > 0 && <div className="my-1 h-px bg-line-faint" />}
                <button
                  type="button"
                  role="option"
                  tabIndex={-1}
                  data-row={shown.length}
                  aria-selected={at === shown.length}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={hover(shown.length)}
                  onClick={() => choose(shown.length)}
                  className={cn('mx-1 flex h-[34px] w-[calc(100%-8px)] items-center gap-2 rounded-button px-3',
                    'text-left text-[13px] font-semibold text-accent', at === shown.length && 'bg-hover')}
                >
                  <IconPlus size={14} />
                  {addWords ? addWords(typed) : `Add “${typed}”`}
                </button>
              </>
            )}
            {rows === 0 && (
              <div className="px-3.5 py-2 text-[12.5px] text-text-muted">Nothing matches “{typed}”.</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
